import {useEffect, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {usePickupArrivalConfig} from '@/hooks/useAppConfig'
import {tripService} from '@/services/tripService'
import {isFromAirportTrip, tripPickupPoint} from '@/features/fromAirportArrival/arrivalEligibility'
import {metresBetween} from '@/utils/polyline'
import {MAX_ARRIVAL_ACCURACY_METERS} from '@/hooks/useArrivedAtPickupPoint'

/**
 * "I'M AT THE PICKUP POINT" — the passenger app's half of the backend's reached-pickup signal.
 *
 * The APP owns the geofence: while the trip is in the pre-boarding window it watches the
 * device against `trip.origin`, and the FIRST time it is inside the admin-set radius it calls
 * `POST /passengers/trips/{trip}/reached-pickup` once. The server stamps the trip, tells the
 * driver ("Passenger at the pickup point") and ignores repeats. No button — this is automatic.
 *
 * ⚠️ WHETHER, AND HOW WIDE, COME FROM `/config/app` (`pickup_arrival.passenger.<direction>`),
 * never from a number here. Admin Settings → Pickup Arrival turns each direction on or off and
 * sets `radius_m`. The request is also restricted to FROM-AIRPORT trips in code, so a to-airport
 * or city trip never starts a watch even if the config were switched on for it. See `usePickupArrivalConfig`.
 *
 * ⚠️ THE TRIP'S OWN STAMP ENDS IT. `trip.reached_pickup.passenger_at` set means this (or an
 * earlier install) already reported — survives reloads, so a refresh never re-fires.
 */

/** The pre-boarding window the backend accepts a report in. */
const WATCH_STATUSES = ['Assigned', 'Accepted', 'EnRoute']

/** Two good fixes in a row, as `useArrivedAtPickupPoint`: one browser fix is a coin toss and
    the report is sent once. */
const CONSECUTIVE_FIXES_REQUIRED = 2

/**
 * Should the position be watched for this trip at all — and against what.
 * Read BEFORE the position exists, because it is what switches the location watch on.
 */
export function usePickupGeofenceTarget(trip) {
    const {enabled, radiusM} = usePickupArrivalConfig('passenger', trip?.trip_direction)
    const pickup = trip ? tripPickupPoint(trip) : null
    /* ⚠️ FROM AIRPORT ONLY, whatever else the config switches on: the passenger's "I have arrived"
       request exists for the airport pickup alone. */
    const wanted = !!trip && isFromAirportTrip(trip) && enabled && !!pickup
        && WATCH_STATUSES.includes(trip.status)
        && !trip.reached_pickup?.passenger_at
    return {wanted, radiusM, pickup}
}

/**
 * @param {object|null} trip  the FULL trip (carries `reached_pickup`)
 * @param {{wanted: boolean, radiusM: number|null, pickup: {lat: number, lng: number}|null}} target
 * @param {{lat: number, lng: number, accuracy_m?: number|null}|null} position
 * @returns {string|number|null} the trip id the report just went out for (drives the modal)
 */
export function useReachedPickupGeofence(trip, target, position) {
    const queryClient = useQueryClient()
    const [reportedTripId, setReportedTripId] = useState(null)
    const streak = useRef(0)
    const inFlight = useRef(false)
    const doneFor = useRef(new Set())

    const tripId = trip?.id ?? null
    const {wanted, radiusM, pickup} = target
    /* Primitives for the effect's deps: `pickup` is a fresh object every render, and an effect
       that re-ran on that would count RENDERS as fixes in the streak below. */
    const pickupLat = pickup?.lat
    const pickupLng = pickup?.lng
    const lat = position?.lat
    const lng = position?.lng
    const accuracy = position?.accuracy_m

    useEffect(() => {
        if (!wanted || tripId == null || doneFor.current.has(String(tripId)) || inFlight.current) return
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(pickupLat) || !Number.isFinite(pickupLng)) return

        /* A fix whose own error bar is wider than the radius cannot answer the question the
           radius asks (a laptop's wifi fix routinely reports "here" from kilometres away). */
        const tooVague = Number.isFinite(accuracy) && accuracy > Math.max(MAX_ARRIVAL_ACCURACY_METERS, radiusM)
        const inside = metresBetween({lat, lng}, {lat: pickupLat, lng: pickupLng}) <= radiusM
        if (tooVague || !inside) {
            streak.current = 0
            return
        }
        streak.current += 1
        if (streak.current < CONSECUTIVE_FIXES_REQUIRED) return

        inFlight.current = true
        tripService.reportReachedPickup(tripId, {lat, lng, accuracy_m: accuracy}).then((result) => {
            if (result.success) {
                doneFor.current.add(String(tripId))
                /* `already_reported`: an earlier install got there first — nothing changed, so
                   there is nothing new to tell the passenger. */
                if (!result.data?.reached_pickup?.already_reported) setReportedTripId(tripId)
                queryClient.invalidateQueries({queryKey: ['trip', tripId]})
                queryClient.invalidateQueries({queryKey: ['trips']})
                return
            }
            const key = result.raw?.key
            /* Not in the window any more: stop for good. Outside the radius (server-side
               verification is on and disagrees): keep watching, the next in-radius fix retries. */
            if (key === 'trip_not_active') doneFor.current.add(String(tripId))
            else streak.current = 0
        }).finally(() => { inFlight.current = false })
    }, [wanted, tripId, lat, lng, accuracy, radiusM, pickupLat, pickupLng, queryClient])

    return reportedTripId
}

export default useReachedPickupGeofence
